/**
 * Trovva — Broker Knowledge Base Settings
 *
 * Three ways to feed the AI Brain:
 *   1. Connect Google Drive / OneDrive — auto-sync a folder
 *   2. Upload files — drag & drop PDFs, DOCX, images
 *   3. Manual entry — type or paste text (fallback)
 *
 * Trovva AI reads all sources and makes them searchable by agents.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDate } from '../../lib/dateFormat';
import {
  BookOpen, Plus, Edit2, Trash2, FileText, DollarSign, Shield,
  HelpCircle, Users, FolderOpen, X, Save, Upload, Cloud,
  HardDrive, RefreshCw, CheckCircle, AlertCircle, File,
  Link2, ExternalLink, Search, Filter,
} from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────
const DOC_TYPES = [
  { id: 'policy', label: 'Office Policy', icon: FileText, color: '#6366f1' },
  { id: 'commission', label: 'Commission', icon: DollarSign, color: '#22c55e' },
  { id: 'compliance', label: 'Compliance', icon: Shield, color: '#f59e0b' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, color: '#3b82f6' },
  { id: 'onboarding', label: 'Onboarding', icon: Users, color: '#8b5cf6' },
  { id: 'other', label: 'Other', icon: FolderOpen, color: '#6b7280' },
];

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg';

// ── Helpers ────────────────────────────────────────────────────
async function getHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getTypeInfo(t) {
  return DOC_TYPES.find(d => d.id === t) || DOC_TYPES[5];
}

// ── Styles ─────────────────────────────────────────────────────
const sectionCard = {
  background: theme.colors.card,
  borderRadius: theme.radius.lg,
  border: `1px solid ${theme.colors.border}`,
  padding: 24,
  marginBottom: 16,
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: theme.radius.sm,
  background: theme.colors.primary, color: '#fff',
  border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: theme.radius.sm,
  background: 'transparent', color: theme.colors.text,
  border: `1px solid ${theme.colors.border}`,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function KnowledgeBaseView() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | doc object
  const [form, setForm] = useState({ title: '', content: '', doc_type: 'policy' });
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Cloud storage state
  const [driveConnected, setDriveConnected] = useState(false);
  const [checkingDrive, setCheckingDrive] = useState(true);
  const [syncingDrive, setSyncingDrive] = useState(false);
  const [driveFolderName, setDriveFolderName] = useState('');

  // File upload state
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // { file, status: 'pending'|'uploading'|'done'|'error' }
  const fileInputRef = useRef(null);

  // ── Fetch docs ────────────────────────────────────────────
  const fetchDocs = useCallback(async () => {
    try {
      const h = await getHeaders();
      if (!h) return;
      const resp = await fetch('/api/knowledge-base', { headers: h });
      const data = await resp.json();
      setDocs(data.docs || []);
    } catch (e) { console.error('[KB] fetch error', e); }
    setLoading(false);
  }, []);

  // ── Check Google Drive connection ─────────────────────────
  const checkDriveStatus = useCallback(async () => {
    setCheckingDrive(true);
    try {
      const h = await getHeaders();
      if (!h) { setCheckingDrive(false); return; }
      const resp = await fetch('/api/google-drive?action=status', { headers: h });
      if (resp.ok) {
        const data = await resp.json();
        setDriveConnected(!!data.connected);
        setDriveFolderName(data.folder || 'Trovva Knowledge Base');
      } else {
        setDriveConnected(false);
      }
    } catch (e) {
      setDriveConnected(false);
    }
    setCheckingDrive(false);
  }, []);

  useEffect(() => {
    fetchDocs();
    checkDriveStatus();
  }, [fetchDocs, checkDriveStatus]);

  // ── Save manual doc ───────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const h = await getHeaders();
      if (!h) return;
      if (editing === 'new') {
        await fetch('/api/knowledge-base', { method: 'POST', headers: h, body: JSON.stringify(form) });
      } else {
        await fetch(`/api/knowledge-base?id=${editing.id}`, { method: 'PATCH', headers: h, body: JSON.stringify(form) });
      }
      setEditing(null);
      setForm({ title: '', content: '', doc_type: 'policy' });
      fetchDocs();
    } catch (e) { console.error('[KB] save error', e); }
    setSaving(false);
  };

  // ── Delete doc ────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!confirm('Delete this document? The AI will no longer reference it.')) return;
    try {
      const h = await getHeaders();
      if (!h) return;
      await fetch(`/api/knowledge-base?id=${id}`, { method: 'DELETE', headers: h });
      fetchDocs();
    } catch (e) { console.error('[KB] delete error', e); }
  };

  // ── File upload handlers ──────────────────────────────────
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const newItems = Array.from(files).map(f => ({ file: f, status: 'pending', name: f.name, size: f.size }));
    setUploadQueue(prev => [...prev, ...newItems]);
    setUploading(true);

    const h = await getHeaders();
    if (!h) { setUploading(false); return; }

    for (let i = 0; i < newItems.length; i++) {
      const item = newItems[i];
      setUploadQueue(prev => prev.map(q =>
        q.name === item.name && q.size === item.size ? { ...q, status: 'uploading' } : q
      ));

      try {
        // Read file content for text-based files, or send as base64
        const content = await readFileContent(item.file);
        const docType = guessDocType(item.file.name);

        await fetch('/api/knowledge-base', {
          method: 'POST',
          headers: h,
          body: JSON.stringify({
            title: item.file.name.replace(/\.[^.]+$/, ''),
            content: content,
            doc_type: docType,
            source: 'upload',
            file_name: item.file.name,
            file_size: item.file.size,
          }),
        });

        setUploadQueue(prev => prev.map(q =>
          q.name === item.name && q.size === item.size ? { ...q, status: 'done' } : q
        ));
      } catch (e) {
        console.error('[KB] upload error', e);
        setUploadQueue(prev => prev.map(q =>
          q.name === item.name && q.size === item.size ? { ...q, status: 'error' } : q
        ));
      }
    }

    setUploading(false);
    fetchDocs();

    // Clear completed after 3 seconds
    setTimeout(() => {
      setUploadQueue(prev => prev.filter(q => q.status === 'error'));
    }, 3000);
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.csv')) {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(file);
      } else {
        // For binary files (PDF, DOCX, images), read as base64
        reader.onload = () => {
          const base64 = reader.result.split(',')[1] || reader.result;
          resolve(`[Uploaded file: ${file.name} (${formatFileSize(file.size)})]\n\nTrovva AI will extract and index the contents of this document automatically.`);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      }
    });
  };

  const guessDocType = (fileName) => {
    const lower = fileName.toLowerCase();
    if (lower.includes('commission') || lower.includes('split') || lower.includes('cap')) return 'commission';
    if (lower.includes('policy') || lower.includes('handbook') || lower.includes('manual')) return 'policy';
    if (lower.includes('compliance') || lower.includes('license') || lower.includes('eo') || lower.includes('insurance')) return 'compliance';
    if (lower.includes('faq') || lower.includes('question')) return 'faq';
    if (lower.includes('onboard') || lower.includes('welcome') || lower.includes('checklist')) return 'onboarding';
    return 'other';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  // ── Google Drive sync ─────────────────────────────────────
  const connectGoogleDrive = async () => {
    // Redirect to Google OAuth with drive scope
    window.location.href = '/api/google-auth?scope=drive&redirect=settings_knowledge';
  };

  const syncFromDrive = async () => {
    setSyncingDrive(true);
    try {
      const h = await getHeaders();
      if (!h) return;
      const resp = await fetch('/api/google-drive?action=list_knowledge', { headers: h });
      if (resp.ok) {
        const data = await resp.json();
        // Files from drive will be auto-imported by the API
        fetchDocs();
      }
    } catch (e) {
      console.error('[KB] drive sync error', e);
    }
    setSyncingDrive(false);
  };

  // ── Editing helpers ───────────────────────────────────────
  const startEdit = (doc) => {
    setEditing(doc);
    setForm({ title: doc.title || '', content: doc.content || '', doc_type: doc.doc_type || 'policy' });
  };

  const startNew = () => {
    setEditing('new');
    setForm({ title: '', content: '', doc_type: 'policy' });
  };

  // ── Filtering ─────────────────────────────────────────────
  const filtered = docs
    .filter(d => filterType === 'all' || d.doc_type === filterType)
    .filter(d => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (d.title || '').toLowerCase().includes(q);
    });

  // ── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 32, color: theme.colors.textLight, textAlign: 'center' }}>
        Loading knowledge base...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: '0 0 6px' }}>
          <BookOpen size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Knowledge Base
        </h2>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: 0, lineHeight: 1.6 }}>
          Feed the AI Brain your brokerage policies, commission schedules, and FAQs.
          Agents get instant answers — your phone stops ringing.
        </p>
      </div>

      {/* ══════════════════════════════════════════════════════
           SECTION 1: Connect Cloud Storage
           ══════════════════════════════════════════════════════ */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Cloud size={16} color={theme.colors.primary} />
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
            Connect Cloud Storage
          </h3>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            background: `${theme.colors.info}15`, color: theme.colors.info,
          }}>
            Recommended
          </span>
        </div>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '0 0 16px', lineHeight: 1.5 }}>
          Point Trovva at your existing document folder. We'll sync automatically — no uploading needed.
        </p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* Google Drive card */}
          <div style={{
            flex: '1 1 220px', padding: 16, borderRadius: theme.radius.md,
            border: `1.5px solid ${driveConnected ? theme.colors.success + '40' : theme.colors.border}`,
            background: driveConnected ? `${theme.colors.success}06` : theme.colors.card,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <HardDrive size={18} color={theme.colors.info} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Google Drive</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                  {checkingDrive ? 'Checking...' : driveConnected ? 'Connected' : 'Not connected'}
                </div>
              </div>
              {driveConnected && (
                <CheckCircle size={16} color={theme.colors.success} style={{ marginLeft: 'auto' }} />
              )}
            </div>

            {driveConnected ? (
              <div>
                <div style={{
                  fontSize: 12, color: theme.colors.textLight, marginBottom: 10,
                  padding: '6px 10px', background: theme.colors.bg, borderRadius: 6,
                }}>
                  Syncing from: <strong style={{ color: theme.colors.text }}>{driveFolderName}</strong>
                </div>
                <button
                  onClick={syncFromDrive}
                  disabled={syncingDrive}
                  style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px', opacity: syncingDrive ? 0.6 : 1 }}
                >
                  <RefreshCw size={12} className={syncingDrive ? 'spin' : ''} />
                  {syncingDrive ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            ) : (
              <button onClick={connectGoogleDrive} style={{ ...btnPrimary, fontSize: 12, padding: '6px 14px' }}>
                <Link2 size={12} /> Connect Google Drive
              </button>
            )}
          </div>

          {/* OneDrive card */}
          <div style={{
            flex: '1 1 220px', padding: 16, borderRadius: theme.radius.md,
            border: `1.5px solid ${theme.colors.border}`,
            background: theme.colors.card, opacity: 0.8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: '#f5f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <Cloud size={18} color="#0078d4" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>OneDrive</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight }}>Microsoft 365</div>
              </div>
            </div>
            <div style={{
              fontSize: 12, color: theme.colors.textLight,
              padding: '6px 10px', background: theme.colors.bg, borderRadius: 6, marginBottom: 10,
            }}>
              Coming soon — already connected via Microsoft 365 integration
            </div>
            <button disabled style={{ ...btnSecondary, fontSize: 12, padding: '6px 14px', opacity: 0.5, cursor: 'default' }}>
              <Link2 size={12} /> Connect OneDrive
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
           SECTION 2: Upload Files
           ══════════════════════════════════════════════════════ */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Upload size={16} color={theme.colors.primary} />
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
            Upload Files
          </h3>
        </div>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '0 0 14px', lineHeight: 1.5 }}>
          Drop your PDFs, Word documents, or spreadsheets here. Trovva AI reads and indexes them automatically.
        </p>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          style={{
            padding: '28px 20px',
            borderRadius: theme.radius.md,
            border: `2px dashed ${dragOver ? theme.colors.primary : theme.colors.border}`,
            background: dragOver ? `${theme.colors.primary}06` : theme.colors.bg,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            onChange={e => handleFiles(e.target.files)}
            style={{ display: 'none' }}
          />
          <Upload size={28} color={dragOver ? theme.colors.primary : theme.colors.textLight} style={{ marginBottom: 8, opacity: 0.6 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text, marginBottom: 4 }}>
            {dragOver ? 'Drop files here' : 'Drag & drop files, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>
            PDF, Word, Excel, CSV, TXT, or images — up to 10 MB each
          </div>
        </div>

        {/* Upload queue */}
        {uploadQueue.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {uploadQueue.map((item, idx) => (
              <div key={`${item.name}-${idx}`} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 8,
                background: item.status === 'done' ? `${theme.colors.success}08` :
                  item.status === 'error' ? `${theme.colors.danger}08` : theme.colors.bg,
                border: `1px solid ${
                  item.status === 'done' ? theme.colors.success + '30' :
                  item.status === 'error' ? theme.colors.danger + '30' : theme.colors.border
                }`,
              }}>
                {item.status === 'done' ? <CheckCircle size={14} color={theme.colors.success} /> :
                 item.status === 'error' ? <AlertCircle size={14} color={theme.colors.danger} /> :
                 item.status === 'uploading' ? <RefreshCw size={14} color={theme.colors.primary} /> :
                 <File size={14} color={theme.colors.textLight} />}
                <span style={{ flex: 1, fontSize: 13, color: theme.colors.text }}>{item.name}</span>
                <span style={{ fontSize: 11, color: theme.colors.textLight }}>{formatFileSize(item.size)}</span>
                <span style={{
                  fontSize: 11, fontWeight: 500,
                  color: item.status === 'done' ? theme.colors.success :
                    item.status === 'error' ? theme.colors.danger : theme.colors.textLight,
                }}>
                  {item.status === 'done' ? 'Done' :
                   item.status === 'error' ? 'Failed' :
                   item.status === 'uploading' ? 'Processing...' : 'Queued'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* AI info banner */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          marginTop: 14, padding: '10px 14px', borderRadius: 8,
          background: `${theme.colors.info}08`, border: `1px solid ${theme.colors.info}20`,
        }}>
          <AlertCircle size={14} color={theme.colors.info} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: theme.colors.info, lineHeight: 1.5 }}>
            Trovva AI will extract text from uploaded documents and make them searchable by your agents.
            PDFs and scanned images are processed using OCR.
          </span>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
           SECTION 3: Manual Entry (always available)
           ══════════════════════════════════════════════════════ */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color={theme.colors.primary} />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: 0 }}>
              Manual Entry
            </h3>
          </div>
          {!editing && (
            <button onClick={startNew} style={{ ...btnPrimary, fontSize: 12, padding: '6px 14px' }}>
              <Plus size={12} /> Add Document
            </button>
          )}
        </div>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '0 0 14px', lineHeight: 1.5 }}>
          Type or paste your policies, commission schedules, and FAQs directly.
          Great for quick answers you want the AI to know right away.
        </p>

        {/* Editor */}
        {editing && (
          <div style={{
            padding: 20, borderRadius: theme.radius.md,
            border: `1.5px solid ${theme.colors.primary}30`,
            background: `${theme.colors.primary}04`,
            marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: theme.colors.text }}>
                {editing === 'new' ? 'New Document' : 'Edit Document'}
              </h4>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={16} color={theme.colors.textLight} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Document title (e.g., 'Commission Schedule 2026')"
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: theme.radius.sm,
                  border: `1px solid ${theme.colors.border}`, fontSize: 14,
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <select
                value={form.doc_type}
                onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))}
                style={{
                  padding: '8px 12px', borderRadius: theme.radius.sm,
                  border: `1px solid ${theme.colors.border}`, fontSize: 13,
                  fontFamily: 'inherit', minWidth: 140,
                }}
              >
                {DOC_TYPES.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>

            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Paste your policy, FAQ, commission schedule, or instructions here.&#10;&#10;Example: 'All agents receive a 70/30 split until they close $5M in volume, then they move to 80/20. Cap is $25,000 per year.'"
              rows={10}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm,
                border: `1px solid ${theme.colors.border}`, fontSize: 13,
                fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6,
                boxSizing: 'border-box', outline: 'none',
              }}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.content.trim()}
                style={{ ...btnPrimary, opacity: (saving || !form.title.trim() || !form.content.trim()) ? 0.5 : 1 }}
              >
                <Save size={14} /> {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setEditing(null)} style={btnSecondary}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
           DOCUMENTS LIST — all sources unified
           ══════════════════════════════════════════════════════ */}
      {docs.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {/* Stats + filter bar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 14, flexWrap: 'wrap', gap: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                Your Documents
              </span>
              <span style={{
                fontSize: 12, fontWeight: 500, padding: '2px 8px',
                borderRadius: 10, background: `${theme.colors.primary}10`,
                color: theme.colors.primary,
              }}>
                {docs.length}
              </span>
            </div>

            {/* Search */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
              minWidth: 180,
            }}>
              <Search size={13} color={theme.colors.textLight} />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search docs..."
                style={{
                  border: 'none', outline: 'none', fontSize: 12,
                  color: theme.colors.text, background: 'transparent',
                  fontFamily: 'inherit', width: '100%',
                }}
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {[{ id: 'all', label: 'All' }, ...DOC_TYPES].map(t => {
              const count = t.id === 'all' ? docs.length : docs.filter(d => d.doc_type === t.id).length;
              if (t.id !== 'all' && count === 0) return null;
              return (
                <button
                  key={t.id}
                  onClick={() => setFilterType(t.id)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                    border: `1px solid ${filterType === t.id ? theme.colors.primary : theme.colors.border}`,
                    background: filterType === t.id ? `${theme.colors.primary}10` : 'white',
                    color: filterType === t.id ? theme.colors.primary : theme.colors.textLight,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  {t.label}
                  {count > 0 && <span style={{ opacity: 0.6 }}>({count})</span>}
                </button>
              );
            })}
          </div>

          {/* Document list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.length === 0 ? (
              <div style={{
                padding: '24px 16px', textAlign: 'center',
                color: theme.colors.textLight, fontSize: 13,
                border: `1px dashed ${theme.colors.border}`, borderRadius: theme.radius.md,
              }}>
                No documents match your search.
              </div>
            ) : (
              filtered.map(doc => {
                const type = getTypeInfo(doc.doc_type);
                const Icon = type.icon;
                const isUploaded = doc.source === 'upload' || doc.file_name;
                return (
                  <div key={doc.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', borderRadius: theme.radius.md,
                    border: `1px solid ${theme.colors.border}`,
                    background: theme.colors.card,
                    transition: 'border-color 0.15s',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: `${type.color}12`, color: type.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                        {doc.title}
                      </div>
                      <div style={{ fontSize: 12, color: theme.colors.textLight, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{type.label}</span>
                        <span>&middot;</span>
                        {isUploaded && (
                          <>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                              <Upload size={10} /> Uploaded
                            </span>
                            <span>&middot;</span>
                          </>
                        )}
                        <span>{formatDate(doc.updated_at || doc.created_at)}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => startEdit(doc)}
                        title="Edit"
                        style={{
                          padding: 6, borderRadius: 6, background: 'none',
                          border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
                        }}
                      >
                        <Edit2 size={14} color={theme.colors.textLight} />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        title="Delete"
                        style={{
                          padding: 6, borderRadius: 6, background: 'none',
                          border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={14} color="#ef4444" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ── Empty state (no docs at all) ───────────────────── */}
      {docs.length === 0 && !editing && (
        <div style={{
          textAlign: 'center', padding: '40px 24px', marginTop: 8,
          border: `1.5px dashed ${theme.colors.border}`, borderRadius: theme.radius.lg,
          background: theme.colors.card,
        }}>
          <BookOpen size={36} color={theme.colors.textLight} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text, margin: '0 0 6px' }}>
            No documents yet
          </p>
          <p style={{ fontSize: 13, color: theme.colors.textLight, margin: '0 0 20px', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            Connect your Google Drive, upload files, or type your policies above.
            The AI Brain will use everything here to answer your agents' questions.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {!driveConnected && (
              <button onClick={connectGoogleDrive} style={btnPrimary}>
                <HardDrive size={14} /> Connect Google Drive
              </button>
            )}
            <button onClick={() => fileInputRef.current?.click()} style={btnSecondary}>
              <Upload size={14} /> Upload Files
            </button>
            <button onClick={startNew} style={btnSecondary}>
              <Plus size={14} /> Type Manually
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
